import React, { useMemo } from 'react';
import type { IntegrationConnection } from '../../../../common/utils/integrations-client/jira/connections';
import { Badge, Button, Panel, PanelContent } from '@deque/cauldron-react';
import { useHistory } from 'react-router-dom';
import { type TFunction, useTranslation } from 'react-i18next';
import styles from './ConnectionCard.css';
import type { IntegrationProductInfo } from '../../../../common/utils/get-integration-product-name-from-slug';
import type { v2 } from '@deque/billing-service-client';
import type { AuthUser } from '../../../../common/contexts/auth';
import type { getActionsFn } from '../../../utils/getJiraOnPremActions';
import { ConnectionActionsMenu } from './ConnectionActionsMenu';

export const getStatusMap = (t: TFunction) => ({
  connected: t('Connected'),
  disconnected: t('Disconnected'),
  deleted: t('Deleted'),
  notConfigured: t('Not configured')
});

export type ConnectionCardProps = {
  connection: IntegrationConnection | null;
  integrationProductInfo: IntegrationProductInfo;
  enterprise: v2.Enterprise;
  canManageIntegrations: boolean;
  authUser: AuthUser;
  connectionsUpdated: () => void;
  getActions: getActionsFn;
};
const ConnectionCard = ({ connection, ...rest }: ConnectionCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const statusMap = useMemo(() => getStatusMap(t), [t]);

  const baseUrl = connection?.base_url;
  const isConnected = connection?.status === 'connected';

  const { displayName } = rest.integrationProductInfo;
  const redirectToTemplates = () =>
    history.push(`/configuration/integrations/${connection?.id}/templates`);

  return connection ? (
    <Panel padding={false}>
      <PanelContent className={styles.cardContent}>
        <div className={styles.heading}>
          <h4>
            {connection.name ||
              t('{{displayName}} connection', {
                displayName
              })}
          </h4>
          <Badge size="small" className={styles.statusBadge}>
            {!baseUrl ? statusMap.notConfigured : statusMap[connection.status]}
          </Badge>
          <ConnectionActionsMenu connection={connection} {...rest} />
        </div>
        <div>
          <p>{baseUrl}</p>
          {isConnected && (
            <Button
              variant="primary"
              onClick={redirectToTemplates}
              aria-label={t(
                'Configure {{displayName}} templates for {{baseUrl}}',
                { displayName, baseUrl }
              )}
            >
              {t('Configure and manage templates')}
            </Button>
          )}
        </div>
      </PanelContent>
    </Panel>
  ) : null;
};

export default ConnectionCard;
